/**
 * Rental block controller
 *
 * Drives the rentals.liquid block: date picker, availability fetch, auto owner
 * selection (endless-owned + best condition first), rent/buy mode toggle,
 * header price update, ATC + BOOK NOW gating, condition tab population.
 *
 * Public surface (consumed by rentals.liquid markup):
 *   .rental-dates-block            — root element
 *   <script type="application/json" data-rental-variant-data> — variant data dump
 *   data-api-url attr on root      — endless API base URL
 *
 * Selectors used in other theme files (must exist for full integration):
 *   [data-rent-item-radio]         — Rent toggle radio (buy-buttons.liquid)
 *   [data-buy-item-radio]          — Buy toggle radio
 *   [data-rental-product-header]   — header wrapper (_product-details.liquid)
 *   [data-rental-header-rent|buy|rrp] — price spans inside header
 *   [data-condition-content]       — condition tab pane (product-info-tabs.liquid)
 *   [ref="addToCartButton"]        — Horizon ATC button
 *   #rental-button-wrapper, #rental-button-btn — optional BOOK NOW button
 *
 * Globals exposed:
 *   window.getRentalIntent()  — true/false/null
 *   window.getRentalData()    — { start_date, end_date, days, owner_uid, ... } | null
 *   window.getResaleData()    — { owner_uid, owner_name, variant_id, price } | null
 *   window.clearOwnerSelection()
 */

import * as RentalUtils from './rental-helpers.js';
import { ERROR_MESSAGES, RENTAL_RULES } from './rental-config.js';
import { API_CONFIG, UI_CONFIG, LOG_PREFIXES, getApiUrl } from './api-config.js';
import * as DatePicker from './rental-datepicker.js';
import { checkVariantAvailability } from './rental-api.js';

const root = document.querySelector('.rental-dates-block');
if (!root) {
  // Block not on page (retail product or different template) — no-op
} else {
  initRentalBlock(root);
}

function initRentalBlock(root) {
  // DOM elements (scoped to root, except price columns which live in their own block above buy-buttons)
  const $ = (cls) => root.querySelector('.' + cls);
  const $$ = (cls) => document.querySelector('.' + cls);
  const startInput = root.querySelector('input[name="rental_start_date"]');
  const endInput = root.querySelector('input[name="rental_end_date"]');
  const availabilityEl = $('rental-availability');
  const daysEl = $('rental-days');
  const resalePriceEl = $$('resale-price');
  const priceEl = $$('rental-price');
  const rentalPriceColumn = $$('rental-price-column');
  const resalePriceColumn = $$('resale-price-column');

  // Required elements — calendar + dates inputs come from rentals block; price elements are optional (rental-resale-prices block can be omitted)
  if ([startInput, endInput, availabilityEl, daysEl].some(el => !el)) return;

  // Variant data + API URL from server-rendered JSON + data-attr
  const dataEl = root.querySelector('script[data-rental-variant-data]');
  const variantData = dataEl ? JSON.parse(dataEl.textContent || '{}') : {};
  const variantRentalPrices = variantData.rentalPrices || {};
  const variantResalePrices = variantData.resalePrices || {};
  const variantOwners = variantData.owners || {};
  const variantRetailPrices = variantData.retailPrices || {};
  const variantOptions = variantData.variantOptions || {};
  const productOptionNames = variantData.productOptionNames || [];
  const productTags = variantData.productTags || [];
  const productFastDeliveryAvailable = !!variantData.fastDeliveryAvailable;

  // Resolve the Buy-sibling variant (resale_price lives only on the Buy variant).
  // Match by every non-duration option (typically Size).
  const isDurationOption = (v) => !!(v && /^(?:\d+\s*days?|buy)$/i.test(String(v).trim()));
  const isBuyOption = (v) => !!(v && /^buy$/i.test(String(v).trim()));
  const resolveBuySiblingId = (variantId) => {
    const opts = variantOptions[variantId];
    if (!opts) return null;
    const nonDur = ['option1', 'option2', 'option3'].map(k => isDurationOption(opts[k]) ? null : (opts[k] || null));
    for (const candidateId of Object.keys(variantOptions)) {
      const c = variantOptions[candidateId];
      const hasBuy = ['option1', 'option2', 'option3'].some(k => isBuyOption(c[k]));
      if (!hasBuy) continue;
      const cNonDur = ['option1', 'option2', 'option3'].map(k => isDurationOption(c[k]) ? null : (c[k] || null));
      if (nonDur[0] === cNonDur[0] && nonDur[1] === cNonDur[1] && nonDur[2] === cNonDur[2]) {
        return candidateId;
      }
    }
    return null;
  };
  // Returns the resale data row for the Size sibling. Falls back to direct
  // lookup so Buy-variant selection still resolves correctly.
  const getResalePricesForVariant = (variantId) => {
    if (variantResalePrices[variantId]) return variantResalePrices[variantId];
    const sib = resolveBuySiblingId(variantId);
    return sib ? variantResalePrices[sib] : null;
  };


  // Centralised labels — read once from data-rental-labels JSON dump in rentals.liquid
  const labelsScript = root.querySelector('script[data-rental-labels]');
  let LABELS = {};
  if (labelsScript) {
    try { LABELS = JSON.parse(labelsScript.textContent || '{}'); } catch (_) { LABELS = {}; }
  }
  const L = (key, fallback) => (LABELS[key] != null && LABELS[key] !== '' ? LABELS[key] : fallback);

  const currencySymbol = L('currency', 'AED');
  const dateRegex = RentalUtils.DATE_REGEX;
  const apiUrl = getApiUrl(root.dataset.apiUrl || '');

  // Captured before anything (loading spinner, condition text) can touch the panel, so a
  // rentable item can always be restored to its original server-rendered copy rather than
  // relying on nothing having written to it yet.
  const conditionPanelDefaultHTML = document.querySelector('[data-condition-content]')?.innerHTML ?? null;

  // State
  let selectedOwnerUid = null;
  let selectedOwnerName = null;
  // Whether the currently-selected (preferred) owner offers rental — drives whether the
  // Condition tab shows the generic rental copy or the owner's real BTS condition.
  let selectedOwnerRentable = null;
  let ownerConditionMap = {};
  let ownerItemCountMap = {};
  let checkTimeout;
  let currentAvailabilityRequest = null;
  // True while a valid date range is being checked. Gates the calendar Done button so the
  // result resolves (shown in the hint) before the calendar can close. Reset in every exit path.
  let availabilityChecking = false;

  const startDateStr = '';
  const endDateStr = '';

  const getVariantId = () => {
    // Anchor to the rental section form. Generic `form[action*="/cart/add"]`
    // can match upsell or related-product forms on the PDP and return their
    // variant ID instead.
    const variantInput =
      document.querySelector('form.product-rental__form input[name="id"]')
      || document.querySelector('.product-rental form[action*="/cart/add"] input[name="id"]');
    if (variantInput) return variantInput.value;
    if (typeof product !== 'undefined' && product.selected_or_first_available_variant) {
      return product.selected_or_first_available_variant.id;
    }
    return null;
  };

  const initializeOwnerCounts = (variantId) => {
    // Clear before the owners lookup, not after, so switching to a variant with no
    // owners entry doesn't leave the previous variant's counts/conditions in place.
    ownerItemCountMap = {};
    ownerConditionMap = {};

    const owners = variantOwners[variantId];
    if (!owners || !Array.isArray(owners)) return;

    owners.forEach(owner => {
      if (owner.item_count > 0) {
        ownerItemCountMap[owner.owner_uid] = owner.item_count;
      }
    });
  };

  const showNoOwnersState = () => {
    availabilityEl.className = 'rental-availability unavailable';
    availabilityEl.textContent = ERROR_MESSAGES.NO_ITEMS_AVAILABLE;
    // Only surface error when user has actually picked dates — avoids showing on initial load
    const datesPicked = startInput?.value && endInput?.value &&
      RentalUtils.validateDates(startInput.value, endInput.value, dateRegex);
    availabilityEl.style.display = datesPicked ? 'block' : 'none';
    selectedOwnerUid = null;
    selectedOwnerName = null;
    selectedOwnerRentable = null;
    updateConditionPanel();
    updatePickerMinDate();
    updateRentalButton();
  };

  const updateVariantOwners = () => {
    const variantId = getVariantId();
    const owners = variantOwners[variantId];
    const rentalIntent = window.getRentalIntent();

    if (!variantId || !owners || !Array.isArray(owners) || owners.length === 0) {
      showNoOwnersState();
      return;
    }

    let filteredOwners = owners;
    if (rentalIntent !== null) {
      filteredOwners = owners.filter(owner => {
        const itemCount = ownerItemCountMap[owner.owner_uid] || 0;
        if (itemCount === 0) return false;
        return rentalIntent ? owner.rental_available === true : owner.resale_available === true;
      });
    }

    if (filteredOwners.length === 0) {
      showNoOwnersState();
      return;
    }

    const totalFilteredItems = filteredOwners.reduce((sum, owner) =>
      sum + (ownerItemCountMap[owner.owner_uid] || 0), 0
    );

    if (totalFilteredItems > 0) {
      // Positive confirmation below the datepicker — only once dates are picked, so it
      // never shows on initial load.
      const datesPicked = startInput?.value && endInput?.value &&
        RentalUtils.validateDates(startInput.value, endInput.value, dateRegex);
      availabilityEl.className = 'rental-availability available';
      availabilityEl.textContent = ERROR_MESSAGES.AVAILABLE;
      availabilityEl.style.display = datesPicked ? 'block' : 'none';
    }

    const sortedOwners = RentalUtils.sortOwnersByCondition(filteredOwners, ownerConditionMap);
    const picked = sortedOwners[0] || null;
    selectedOwnerUid = picked?.owner_uid || null;
    selectedOwnerName = picked ? (picked.storefront_name || picked.owner_name || null) : null;
    selectedOwnerRentable = picked ? picked.rental_available === true : null;
    updateConditionPanel();
    updateManagedByBadge();
    updatePickerMinDate();
    updateRentalButton();
  };

  const updateManagedByBadge = () => {
    const badgeEl = document.querySelector('[data-managed-by]');
    if (!badgeEl) return;
    if (!selectedOwnerUid) return;
    const isEndless = RentalUtils.isEndlessOwnerById(selectedOwnerUid, selectedOwnerName);
    if (isEndless) {
      badgeEl.textContent = L('managedByEndless', 'MANAGED BY ENDLESS');
    } else {
      badgeEl.innerHTML = L('managedByOwnerHtml', 'MANAGED BY OWNER');
    }
  };

  const updatePickerMinDate = () => {
    if (!startPicker || typeof startPicker.set !== 'function') return;

    const today = RentalUtils.startOfToday();
    const hasAvailabilityWaiver = productTags.includes('Product_Available_Today');
    // Product_Available_Tomorrow: the seller confirmed but we cannot deliver today (set by the
    // internal platform's availability check), so the owner lead time is waived from tomorrow only.
    const hasTomorrowWaiver = productTags.includes('Product_Available_Tomorrow');
    const ownerLeadDays = hasAvailabilityWaiver
      ? 0
      : hasTomorrowWaiver
        ? Math.min(1, RENTAL_RULES.NON_ENDLESS_BOOKING_LEAD_DAYS)
        : RENTAL_RULES.NON_ENDLESS_BOOKING_LEAD_DAYS;
    let minDate = today;
    let isEndless = true;

    if (selectedOwnerUid) {
      isEndless = RentalUtils.isEndlessOwnerById(selectedOwnerUid, selectedOwnerName) || productFastDeliveryAvailable;
      if (!isEndless) {
        minDate = RentalUtils.addDays(today, ownerLeadDays);
      }
    } else {
      // No owner selected yet (page load): apply the lead buffer only when no
      // Endless owner exists (Endless has no buffer + wins once availability resolves).
      const variantId = getVariantId();
      const owners = variantId ? variantOwners[variantId] : null;
      if (Array.isArray(owners) && owners.length > 0) {
        const hasEndless = owners.some(o =>
          RentalUtils.isEndlessOwnerById(o.owner_uid, o.storefront_name || o.owner_name)
        );
        if (!hasEndless) {
          isEndless = productFastDeliveryAvailable;
          if (!isEndless) {
            minDate = RentalUtils.addDays(today, ownerLeadDays);
          }
        }
      }
    }

    const existingStart = RentalUtils.parseDate(startInput.value);
    const existingEnd = RentalUtils.parseDate(endInput.value);
    startPicker.set('minDate', minDate);
    // Re-apply selected range — flatpickr clears visual highlight on minDate change
    if (existingStart && existingEnd && existingStart >= minDate) {
      if (typeof startPicker.setDate === 'function') startPicker.setDate([existingStart, existingEnd], false);
    } else if (existingStart && existingStart >= minDate) {
      if (typeof startPicker.setDate === 'function') startPicker.setDate([existingStart], false);
    }

    // Anchor the calendar view to minDate's month while no dates are picked.
    // Once a start date is picked the view is left in place.
    const hasUserSelection = !!existingStart;
    const restoreView = () => {
      if (hasUserSelection) return;
      if (typeof startPicker.jumpToDate === 'function') {
        startPicker.jumpToDate(minDate);
      } else {
        startPicker.currentMonth = minDate.getMonth();
        startPicker.currentYear = minDate.getFullYear();
        if (typeof startPicker.redraw === 'function') startPicker.redraw();
      }
    };
    restoreView();
    // Defer to win against flatpickr's async redraw post-set
    setTimeout(restoreView, 0);

    const noteEl = document.querySelector('[data-booking-note]');
    if (noteEl) {
      if (isEndless) {
        noteEl.textContent = L('noteEndless', 'We recommend booking your rental for 1 day before your event date. It will be sent so it arrives to you on or before your selected start date.');
      } else if (hasAvailabilityWaiver || hasTomorrowWaiver) {
        noteEl.textContent = '';
      } else {
        const earliest = minDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        const tpl = L('noteOwner', 'This item is owner-managed and requires %days% days advance notice. The earliest drop-off date is %date%.');
        noteEl.textContent = tpl
          .replace('%days%', RENTAL_RULES.NON_ENDLESS_BOOKING_LEAD_DAYS)
          .replace('%date%', earliest);
      }
    }

    // Clear any selected start date that is now before the new minimum
    if (startInput.value) {
      const startDate = RentalUtils.parseDate(startInput.value);
      if (startDate && startDate < minDate) {
        startPicker.clear();
        startInput.value = '';
        endInput.value = '';
        updateDateSummary();
        updateRentalPrice();
      }
    }
  };

  const pickBestCondition = () => {
    const entries = Object.values(ownerConditionMap || {});
    if (entries.length === 0) return null;
    return entries.reduce((best, cur) => {
      const bestRank = RentalUtils.CONDITION_SEVERITY[best.name] ?? 999;
      const curRank = RentalUtils.CONDITION_SEVERITY[cur.name] ?? 999;
      return curRank < bestRank ? cur : best;
    });
  };

  const renderConditionPanel = (cond) => {
    const panel = document.querySelector('[data-condition-content]');
    if (!panel || !cond) return;
    const safeName = RentalUtils.escapeHtml(cond.name);
    const safeDesc = RentalUtils.escapeHtml(cond.description);
    const descHtml = safeDesc ? `<br>${safeDesc}` : '';
    panel.innerHTML = `<p><strong>This item has been listed as ${safeName}</strong>${descHtml}</p>`;
  };

  // Purchase-only items (the preferred/selected owner isn't rentable) get the real BTS
  // condition instead of the generic "available to rent" copy. Rentable items are restored
  // to the original server-rendered copy — not just left alone — because the loading
  // spinner (renderConditionLoading) may already have overwritten the panel by this point.
  const updateConditionPanel = () => {
    const panel = document.querySelector('[data-condition-content]');
    if (!panel) return;
    if (selectedOwnerRentable === true) {
      if (conditionPanelDefaultHTML != null) panel.innerHTML = conditionPanelDefaultHTML;
      return;
    }
    const cond = (selectedOwnerUid && ownerConditionMap[selectedOwnerUid]) || pickBestCondition();
    if (cond) {
      renderConditionPanel(cond);
      return;
    }
    panel.innerHTML = `<p class="product-info-tabs__condition-empty">${L('conditionEmptyBuy', 'Condition info unavailable for this item.')}</p>`;
  };

  const OUT_OF_STOCK_LABEL = '—';

  const renderPrice = (el, price) => {
    if (!el) return;
    el.classList.remove('hidden');
    if (!price || price <= 0) {
      el.textContent = OUT_OF_STOCK_LABEL;
      return;
    }
    el.textContent = `\u20C3${RentalUtils.formatPrice(price)}`;
  };

  const updateResalePrice = () => {
    if (!resalePriceEl) return;
    const variantId = getVariantId();
    const resaleData = getResalePricesForVariant(variantId);
    const price = resaleData?.resale_price ? parseFloat(resaleData.resale_price) : 0;
    renderPrice(resalePriceEl, price);
  };

  const renderFromPrice = (rentalPrices) => {
    if (!priceEl) return;
    const fromPrice = rentalPrices?.three_day_price ? parseFloat(rentalPrices.three_day_price) : 0;
    if (fromPrice > 0) {
      renderPrice(priceEl, fromPrice);
    } else {
      renderPrice(priceEl, 0);
    }
  };

  const updateRentalPrice = () => {
    if (!priceEl) return;
    const start = startInput.value;
    const end = endInput.value;
    const variantId = getVariantId();
    const rentalPrices = variantRentalPrices[variantId];

    priceEl.classList.remove('hidden');

    if (!RentalUtils.validateDates(start, end, dateRegex)) {
      renderFromPrice(rentalPrices);
      return;
    }

    const days = RentalUtils.calculateRentalDays(start, end);
    if (!days || days < 3 || days > 30) {
      renderFromPrice(rentalPrices);
      return;
    }

    const price = RentalUtils.getRentalPrice(days, rentalPrices);
    if (!price || price <= 0) {
      renderFromPrice(rentalPrices);
      return;
    }
    renderPrice(priceEl, price);
  };

  const updateRentalButton = () => {
    const rentalButtonEl = document.getElementById('rental-button-wrapper');
    const rentalButton = document.getElementById('rental-button-btn');
    if (!rentalButtonEl || !rentalButton) return;

    const variantId = getVariantId();
    const hasValidDates = RentalUtils.validateDates(startInput.value, endInput.value, dateRegex);
    const isAvailable = availabilityEl.classList.contains('available');
    const owners = variantOwners[variantId];
    const hasOwners = owners && Array.isArray(owners) && owners.length > 0;
    const hasSelectedOwner = !hasOwners || selectedOwnerUid !== null;

    rentalButtonEl.classList.remove('hidden');
    rentalButton.disabled = !(hasValidDates && hasSelectedOwner && isAvailable && variantId);
  };

  const updateDaysDisplay = (days) => {
    daysEl.classList.remove('hidden');
    daysEl.textContent = (days && days >= 3 && days <= 30) ? `Duration: ${days} days` : 'Duration: 3 days';
    RentalUtils.RentalDurationManager.updateDuration(days);
  };

  const setErrorState = (errorMsg) => {
    availabilityEl.className = 'rental-availability error';
    availabilityEl.textContent = errorMsg;
    availabilityEl.style.display = 'block';
    selectedOwnerUid = null;
    selectedOwnerName = null;
    // Invalid range — no check to wait for, so free Done and surface the message in the hint.
    availabilityChecking = false;
    updateHint();
    updateDoneState();
    updateRentalButton();
    updateAddToCartButton();
  };

  const validateRangeAndCheck = () => {
    if (!startInput.value || !endInput.value) {
      availabilityChecking = false;
      updateDoneState();
      return;
    }
    if (!RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) {
      availabilityChecking = false;
      updateDoneState();
      return;
    }
    const days = RentalUtils.calculateRentalDays(startInput.value, endInput.value);
    const periodValidation = RentalUtils.validateRentalPeriod(days);
    if (!periodValidation.valid) {
      setErrorState(periodValidation.error);
      return;
    }
    // Valid full range — keep the calendar open, lock Done, and show the checking state.
    availabilityChecking = true;
    updateHint();
    updateDoneState();
    debouncedCheck();
  };

  const checkAvailability = async () => {
    const variantId = getVariantId();
    const start = RentalUtils.normalizeDate(startInput.value);
    const end = RentalUtils.normalizeDate(endInput.value);

    if (currentAvailabilityRequest) {
      currentAvailabilityRequest.abort();
      currentAvailabilityRequest = null;
    }

    if (!start || !end || !dateRegex.test(start) || !dateRegex.test(end)) {
      availabilityChecking = false;
      updateVariantOwners();
      updateRentalButton();
      updateHint();
      updateDoneState();
      return;
    }

    const startDate = RentalUtils.parseDate(start);
    const endDate = RentalUtils.parseDate(end);

    if (!startDate || !endDate) {
      setErrorState(ERROR_MESSAGES.INVALID_DATE);
      return;
    }

    // Skip the API check when the start has aged into the past — server rejects with 400.
    if (startDate < RentalUtils.startOfToday()) {
      availabilityChecking = false;
      updateVariantOwners();
      updateRentalButton();
      updateHint();
      updateDoneState();
      return;
    }

    const days = RentalUtils.calculateRentalDays(start, end);
    const periodValidation = RentalUtils.validateRentalPeriod(days);

    if (!periodValidation.valid) {
      setErrorState(periodValidation.error);
      return;
    }

    availabilityEl.className = 'rental-availability loading';
    availabilityEl.innerHTML = `<span class="rental-availability__spinner" role="status" aria-label="${ERROR_MESSAGES.CHECKING_AVAILABILITY}"></span>${ERROR_MESSAGES.CHECKING_AVAILABILITY}`;
    availabilityEl.style.display = 'block';
    updateDaysDisplay(days);

    if (!apiUrl || !variantId) {
      availabilityChecking = false;
      updateVariantOwners();
      updateRentalButton();
      updateHint();
      updateDoneState();
      return;
    }

    const startForAPI = RentalUtils.formatDateForAPI(start);
    const endForAPI = RentalUtils.formatDateForAPI(end);
    updateRentalButton();

    availabilityChecking = true;
    updateHint();
    updateDoneState();

    currentAvailabilityRequest = new AbortController();

    try {
      const url = `${API_CONFIG.PROXY.AVAILABILITY}?variant_id=${variantId}&start_date=${encodeURIComponent(startForAPI)}&end_date=${encodeURIComponent(endForAPI)}`;
      const response = await fetch(url, { signal: currentAvailabilityRequest.signal });

      if (!response.ok) {
        const errorMap = {
          404: ERROR_MESSAGES.PRODUCT_NOT_FOUND,
          400: ERROR_MESSAGES.INVALID_DATE_RANGE
        };
        throw new Error(errorMap[response.status] || (response.status >= 500 ? ERROR_MESSAGES.SERVICE_UNAVAILABLE : ERROR_MESSAGES.UNABLE_TO_CHECK));
      }

      const data = await response.json();

      if (data.status === 'success' && data.data) {
        const count = data.count || 0;
        const currentRentalIntent = window.getRentalIntent();

        let displayCount = count;
        if (!currentRentalIntent && data.data.items && Array.isArray(data.data.items)) {
          displayCount = data.data.items.filter(item => item.has_resale_price).length;
        }

        if (displayCount > 0) {
          availabilityEl.className = 'rental-availability available';
          availabilityEl.textContent = ERROR_MESSAGES.AVAILABLE;
          availabilityEl.style.display = 'block';
        } else {
          availabilityEl.className = 'rental-availability unavailable';
          availabilityEl.textContent = ERROR_MESSAGES.NO_ITEMS_AVAILABLE;
          availabilityEl.style.display = 'block';
        }

        if (data.data.items && Array.isArray(data.data.items)) {
          ownerItemCountMap = {};
          // Preserve ownerConditionMap across availability calls so the Condition tab keeps showing
          // best-known condition even when the date range returns 0 items or BUY mode filters out all owners.

          data.data.items.forEach(item => {
            if (!item.owner_uid) return;
            const shouldCount = currentRentalIntent || item.has_resale_price;
            if (shouldCount) {
              ownerItemCountMap[item.owner_uid] = (ownerItemCountMap[item.owner_uid] || 0) + 1;
            }
            if (item.condition_name && (!ownerConditionMap[item.owner_uid] || (RentalUtils.CONDITION_SEVERITY[item.condition_name] || 999) < (RentalUtils.CONDITION_SEVERITY[ownerConditionMap[item.owner_uid].name] || 999))) {
              ownerConditionMap[item.owner_uid] = { name: item.condition_name, description: item.condition_description };
            }
          });

          console.log(`${LOG_PREFIXES.RENTAL} Availability:`, {
            items: count,
            owners: Object.keys(ownerItemCountMap).length,
            withConditions: Object.keys(ownerConditionMap).length
          });
        } else {
          ownerConditionMap = {};
        }

        updateVariantOwners();
        updateRentalPrice();
        updateRentalButton();
        updateAddToCartButton();
        updateToggleLabels();
      } else {
        throw new Error(ERROR_MESSAGES.UNABLE_TO_CHECK);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;

      console.error(`${LOG_PREFIXES.RENTAL} Availability check error:`, err.message);
      availabilityEl.className = 'rental-availability error';
      availabilityEl.textContent = err.message || ERROR_MESSAGES.UNABLE_TO_CHECK;
      availabilityEl.style.display = 'block';
      availableOwnerUids = null;
      updateVariantOwners();
      updateRentalButton();
      updateAddToCartButton();
    } finally {
      currentAvailabilityRequest = null;
      availabilityChecking = false;
      updateHint();
      updateDoneState();
    }
  };

  const debouncedCheck = () => {
    clearTimeout(checkTimeout);
    checkTimeout = setTimeout(checkAvailability, UI_CONFIG.DEBOUNCE_TIMEOUT);
  };

  const setRadioDisabled = (radio, disabled) => {
    if (!radio) return;
    radio.disabled = disabled;
    const label = radio.closest('.buy-product-toggle-label');
    if (!label) return;
    label.style.opacity = disabled ? '0.4' : '1';
    label.style.cursor = disabled ? 'not-allowed' : 'pointer';
    label.style.pointerEvents = disabled ? 'none' : 'auto';
  };

  const updateModeRadioStates = (variantId) => {
    const buyRadio = document.querySelector('[data-buy-item-radio]');
    const rentRadio = document.querySelector('[data-rent-item-radio]');

    // Per-size resale drives the auto-flip below but never disables the toggle.
    // Per-size state shows via picker grey-out + ATC gating.
    const resaleData = getResalePricesForVariant(variantId);
    const resalePrice = resaleData?.resale_price ? parseFloat(resaleData.resale_price) : 0;
    let noResale = !resalePrice || resalePrice <= 0;

    const rentalPrices = variantRentalPrices[variantId];
    const hasAnyRentalPrice = rentalPrices && Object.values(rentalPrices).some(v => parseFloat(v) > 0);
    const noRental = !hasAnyRentalPrice;

    const productHasAnyResale = Object.values(variantResalePrices).some(d => d && parseFloat(d.resale_price) > 0);
    const productHasAnyRental = Object.values(variantRentalPrices).some(rp => rp && Object.values(rp.day_prices || {}).some(v => parseFloat(v) > 0));

    setRadioDisabled(buyRadio, !productHasAnyResale);
    setRadioDisabled(rentRadio, !productHasAnyRental);

    // Auto-flip if currently selected mode is now disabled
    if (noResale && buyRadio?.checked && rentRadio && !rentRadio.disabled) {
      rentRadio.checked = true;
      rentRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (noRental && rentRadio?.checked && buyRadio && !buyRadio.disabled) {
      buyRadio.checked = true;
      buyRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const updateHeaderPrices = () => {
    const headerEl = document.querySelector('[data-rental-product-header]');
    if (!headerEl) return;
    const rentEl = headerEl.querySelector('[data-rental-header-rent]');
    const buyEl = headerEl.querySelector('[data-rental-header-buy]');
    const rrpEl = headerEl.querySelector('[data-rental-header-rrp]');
    const variantId = getVariantId();
    const rentalPrices = variantRentalPrices[variantId];

    // Dynamic rental price — reflect selected duration; fall back to base 3-day price
    let rentDays = 3;
    if (RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) {
      const days = RentalUtils.calculateRentalDays(startInput.value, endInput.value);
      if (days && days >= 3 && days <= 30) rentDays = days;
    }
    const rentPrice = RentalUtils.getRentalPrice(rentDays, rentalPrices);
    // RENT headline stays fixed at the 3-day minimum; windowed pricing is carried by dataset.rentDays for the breakdown.
    const fromPrice = RentalUtils.getRentalPrice(3, rentalPrices);

    const resaleData = getResalePricesForVariant(variantId);
    let buyPrice = resaleData?.resale_price ? parseFloat(resaleData.resale_price) : 0;
    const rrpPrice = variantRetailPrices[variantId] ? parseFloat(variantRetailPrices[variantId]) : 0;
    const fmt = (p) => (p && p > 0) ? RentalUtils.formatPrice(p).replace(/\.00$/, '') : '';

    // Header BUY price is informational: cheapest resale across the product,
    // so the headline stays visible on non-buyable sizes (ATC gated per size).
    let buyHeadlinePrice = buyPrice;
    if (!buyHeadlinePrice) {
      const allResale = Object.values(variantResalePrices)
        .map(d => d && parseFloat(d.resale_price))
        .filter(v => Number.isFinite(v) && v > 0);
      if (allResale.length) buyHeadlinePrice = Math.min.apply(null, allResale);
    }

    // RENT renders the fee-inclusive rental metafield; BUY and RRP render bare values.
    if (rentEl) rentEl.textContent = fmt(fromPrice && fromPrice > 0 ? Math.round(fromPrice) : 0);
    if (buyEl) buyEl.textContent = fmt(buyHeadlinePrice > 0 ? Math.round(buyHeadlinePrice) : 0);
    if (rrpEl) rrpEl.textContent = fmt(rrpPrice);

    // Only overwrite with valid values — never clobber the server-rendered base with empty.
    if (rentPrice && rentPrice > 0) headerEl.dataset.baseRent = String(rentPrice);
    if (buyPrice && buyPrice > 0) headerEl.dataset.baseBuy = String(buyPrice);
    headerEl.dataset.rentDays = String(rentDays);

    const entries = [
      { key: 'rent', visible: rentPrice > 0 },
      { key: 'buy', visible: buyHeadlinePrice > 0 },
      // Hide RRP when BUY is at or above it - there is no saving to show.
      { key: 'rrp', visible: rrpPrice > 0 && !(buyHeadlinePrice > 0 && Math.round(buyHeadlinePrice) >= rrpPrice) }
    ];
    entries.forEach(({ key, visible }) => {
      const el = headerEl.querySelector(`[data-rental-header-entry="${key}"]`);
      if (el) el.hidden = !visible;
    });
    // Each sep[i] sits between entries[i] and entries[i+1] — show only when both
    // neighbours are visible, so a hidden RENT doesn't leave a leading "|".
    const seps = headerEl.querySelectorAll('[data-rental-header-sep]');
    seps.forEach((sep, i) => {
      sep.hidden = !(entries[i] && entries[i].visible && entries[i + 1] && entries[i + 1].visible);
    });

    // Dim inactive RENT/BUY entries based on current mode. RRP stays static.
    const rentRadio = document.querySelector('[data-rent-item-radio]');
    const isRenting = rentRadio ? rentRadio.checked : true;
    entries.forEach(({ key }) => {
      if (key === 'rrp') return;
      const el = headerEl.querySelector(`[data-rental-header-entry="${key}"]`);
      if (!el) return;
      const active = key === 'rent' ? isRenting : !isRenting;
      el.style.opacity = active ? '1' : '0.4';
    });
  };

  const updateAll = () => {
    const variantId = getVariantId();
    initializeOwnerCounts(variantId);

    updateResalePrice();
    updateRentalPrice();
    updateHeaderPrices();
    updateModeRadioStates(variantId);
    updateRentalButton();
    updateAddToCartButton();
    updateToggleLabels();
    if (typeof loadBlockedDates === 'function') loadBlockedDates();
    if (typeof loadInitialConditions === 'function') loadInitialConditions();

    // On a rentable item with no dates pre-filled, this runs updateVariantOwners()
    // synchronously in the same tick as loadInitialConditions()'s spinner write above, so
    // updateConditionPanel() restores the static copy before the browser ever paints it. If
    // dates ever arrive pre-filled here (URL param, persisted state), that branch instead
    // goes through the debounced async path and a brief spinner flash becomes possible.
    if (RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) {
      debouncedCheck();
    } else {
      updateVariantOwners();
    }
  };

  const formatBuyPrice = () => {
    RentalUtils.formatBuyPriceElements(currencySymbol);
  };

  const refreshAvailabilityIfNeeded = () => {
    const variantId = getVariantId();
    initializeOwnerCounts(variantId);

    // Skip the API check when the selected start has aged into the past.
    const start = RentalUtils.parseDate(startInput.value);
    const notPast = start && start >= RentalUtils.startOfToday();
    if (notPast && RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) {
      checkAvailability();
    } else {
      updateVariantOwners();
    }
  };

  // DATE PICKER INIT
  const calendarMount = $('rental-calendar');
  const startDisplayEl = $('rental-start-display');
  const endDisplayEl = $('rental-end-display');
  const startTriggerEl = root.querySelector('.rental-date-trigger[data-mode="start"]');
  const endTriggerEl = root.querySelector('.rental-date-trigger[data-mode="end"]');
  const hintEl = $('rental-date-hint');
  const calendarPanel = root.querySelector('.rental-calendar-panel');
  const doneEl = root.querySelector('.rental-date-done');

  const formatTriggerDate = (dateStr) => {
    const parsed = RentalUtils.parseDate(dateStr);
    if (!parsed) return null;
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const renderTrigger = (el, valueStr) => {
    if (!el) return;
    if (valueStr) {
      el.textContent = valueStr;
      el.classList.remove('empty');
    } else {
      el.textContent = 'Select date';
      el.classList.add('empty');
    }
  };

  const updateActiveTrigger = (mode) => {
    // Active outline only shows while the panel is open (matches the prototype).
    const isOpen = !!(calendarPanel && calendarPanel.classList.contains('open'));
    if (startTriggerEl) startTriggerEl.classList.toggle('active', isOpen && mode === 'start');
    if (endTriggerEl) endTriggerEl.classList.toggle('active', isOpen && mode === 'end');
  };

  const updateHint = () => {
    if (!hintEl) return;
    if (!startInput.value) {
      hintEl.textContent = L('hintStart', 'Select your drop-off date');
      hintEl.classList.remove('hidden', 'rental-date-hint--alert');
    } else if (!endInput.value) {
      hintEl.textContent = L('hintEnd', 'Now select your pick-up date');
      hintEl.classList.remove('hidden', 'rental-date-hint--alert');
    } else if (availabilityChecking) {
      hintEl.textContent = ERROR_MESSAGES.CHECKING_AVAILABILITY;
      hintEl.classList.remove('hidden', 'rental-date-hint--alert');
    } else if (availabilityEl.classList.contains('unavailable') || availabilityEl.classList.contains('error')) {
      // Invalid range or no stock — show the message in orange, in the hint area.
      hintEl.textContent = availabilityEl.textContent || ERROR_MESSAGES.NO_ITEMS_AVAILABLE;
      hintEl.classList.remove('hidden');
      hintEl.classList.add('rental-date-hint--alert');
    } else {
      const days = RentalUtils.calculateRentalDays(startInput.value, endInput.value);
      const prefix = L('hintDurationPrefix', 'Duration:');
      // Fee-inclusive rental price for the selected duration (same metafield source
      // as the header price / breakdown "Rental Price" line) — shown alongside the
      // duration so the customer sees a price the moment they finish picking dates,
      // without waiting on the fee-preview panel fetch.
      let priceHtml = '';
      if (days) {
        const price = RentalUtils.getRentalPrice(days, variantRentalPrices[getVariantId()]);
        if (price && price > 0) {
          priceHtml = ` &middot; <span class="rental-date-hint__price">\u20C3${RentalUtils.formatPrice(Math.round(price))}</span>`;
        }
      }
      hintEl.innerHTML = days
        ? `${prefix} <span class="rental-date-hint__days">${days} day${days === 1 ? '' : 's'}</span>${priceHtml}`
        : '';
      hintEl.classList.toggle('hidden', !days);
      hintEl.classList.remove('rental-date-hint--alert');
    }
  };

  const updateDoneState = () => {
    if (!doneEl) return;
    // Done is blocked only while a valid range is being checked; invalid dates can still close.
    doneEl.disabled = availabilityChecking;
  };

  const updateDateSummary = () => {
    renderTrigger(startDisplayEl, formatTriggerDate(startInput.value));
    renderTrigger(endDisplayEl, formatTriggerDate(endInput.value));
    const mode = startInput.value ? 'end' : 'start';
    updateActiveTrigger(mode);
    updateHint();
    // Done turns orange only once both dates are filled.
    if (doneEl) doneEl.classList.toggle('is-ready', !!(startInput.value && endInput.value));
  };
  updateDateSummary();

  // Collapsible calendar — opens on field click, closes after both dates / on outside click.
  const openCalendar = (mode) => {
    if (calendarPanel) calendarPanel.classList.add('open');
    updateActiveTrigger(mode);
    updateHint();
  };
  const closeCalendar = () => {
    if (calendarPanel) calendarPanel.classList.remove('open');
    if (startTriggerEl) startTriggerEl.classList.remove('active');
    if (endTriggerEl) endTriggerEl.classList.remove('active');
  };
  document.addEventListener('click', (e) => {
    if (!calendarPanel || !calendarPanel.classList.contains('open')) return;
    if (!root.contains(e.target)) closeCalendar();
  });
  // Wire Done directly here (not only via the datepicker onDone callback) so it closes
  // even if rental-datepicker.js is cached/stale. Listener survives the appendChild move.
  const doneBtn = root.querySelector('.rental-date-done');
  if (doneBtn) {
    doneBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeCalendar();
    });
  }
  // Clear is an escape hatch — works even mid-check. Abort the in-flight request,
  // drop the gate so Done frees up, and wipe the below-calendar message.
  const clearBtn = root.querySelector('.rental-date-clear');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      if (currentAvailabilityRequest) { currentAvailabilityRequest.abort(); currentAvailabilityRequest = null; }
      clearTimeout(checkTimeout);
      availabilityChecking = false;
      availabilityEl.className = 'rental-availability';
      availabilityEl.textContent = '';
      availabilityEl.style.display = 'none';
      updateHint();
      updateDoneState();
    });
  }

  // Blocked dates keyed by YYYY-MM-DD with type ('booked' | 'manual').
  const blockedDates = {};

  // Fire the blocked-dates fetch immediately (before flatpickr mounts) so buffers appear on first paint.
  const fetchBlockedDates = () => {
    const variantId = getVariantId();
    if (!variantId) return Promise.resolve();
    const today = RentalUtils.startOfToday();
    const maxDate = RentalUtils.addDays(today, RENTAL_RULES.BOOKING_WINDOW_DAYS || 45);
    const url = `${API_CONFIG.PROXY.BLOCKED_DATES}?variant_id=${encodeURIComponent(variantId)}&from=${RentalUtils.formatDateISO(today)}&to=${RentalUtils.formatDateISO(maxDate)}`;
    return fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        const list = json?.data?.blocked_dates || [];
        Object.keys(blockedDates).forEach(k => delete blockedDates[k]);
        list.forEach(({ date, type }) => { blockedDates[date] = type; });
        if (startPicker) DatePicker.applyBlockedClasses(startPicker, blockedDates);
      })
      .catch(err => console.warn(`${LOG_PREFIXES.RENTAL} blocked-dates fetch failed:`, err.message));
  };
  let blockedDatesInflight = fetchBlockedDates();

  const rentId = root.dataset.rentId || 'rental';

  const { startPicker, endPicker } = DatePicker.initializeDatePickers({
    startInput,
    endInput,
    startDateStr,
    endDateStr,
    mountEl: calendarMount,
    rentId,
    blockedDates,
    onStartChange: () => { clearAtcError(); updateDateSummary(); updateRentalPrice(); updateHeaderPrices(); updateToggleLabels(); validateRangeAndCheck(); },
    onEndChange: () => { clearAtcError(); updateDateSummary(); updateRentalPrice(); updateHeaderPrices(); updateToggleLabels(); validateRangeAndCheck(); },
    onDone: () => { closeCalendar(); },
    onStartBlur: (dateStr) => {
      DatePicker.handleStartDateBlur({
        startInput, endInput, endPicker, startPicker, availabilityEl, dateRegex,
        updateRentalPrice, debouncedCheck, checkAvailability, setErrorState, rentId
      }, dateStr);
    },
    onEndBlur: (dateStr) => {
      DatePicker.handleEndDateBlur({
        startInput, endInput, availabilityEl, dateRegex, checkAvailability, setErrorState, rentId
      }, dateStr);
    }
  });

  // Refresh blocked dates — awaits the in-flight prefetch on first call, otherwise re-fetches (variant change).
  const loadBlockedDates = async () => {
    if (blockedDatesInflight) {
      await blockedDatesInflight;
      blockedDatesInflight = null;
    } else {
      await fetchBlockedDates();
    }
    if (startPicker) DatePicker.applyBlockedClasses(startPicker, blockedDates);
  };

  const renderConditionLoading = () => {
    // Don't flash a spinner over the static rental copy when we already know (from the
    // previous cycle) that the current owner is rentable — updateConditionPanel would
    // restore it a tick later anyway, but skipping avoids the visible flicker.
    if (selectedOwnerRentable === true) return;
    const panel = document.querySelector('[data-condition-content]');
    if (!panel) return;
    panel.innerHTML = '<span class="product-info-tabs__condition-spinner" role="status" aria-label="Loading condition"></span>';
  };

  // Pre-load condition info on page load (without picking dates) so the Condition tab shows the best available now.
  // Uses a small default window — only consumes condition_name/description, ignores availability counts.
  const loadInitialConditions = async () => {
    const variantId = getVariantId();
    if (!variantId) return;

    // Start from the booking offset (tomorrow), not today, so a lagging client clock never sends a past start.
    const startDate = RentalUtils.addDays(RentalUtils.startOfToday(), RENTAL_RULES.DEFAULT_START_OFFSET_DAYS || 1);
    const endDate = RentalUtils.addDays(startDate, (RENTAL_RULES.MIN_RENTAL_DAYS || 3) - 1);

    if (Object.keys(ownerConditionMap || {}).length === 0) renderConditionLoading();

    try {
      const result = await checkVariantAvailability(variantId, RentalUtils.formatDate(startDate), RentalUtils.formatDate(endDate), apiUrl);
      if (!result.success) return;
      const items = result.items || [];

      items.forEach((item) => {
        if (!item.owner_uid || !item.condition_name) return;
        const existing = ownerConditionMap[item.owner_uid];
        const newRank = RentalUtils.CONDITION_SEVERITY[item.condition_name] ?? 999;
        const oldRank = existing ? (RentalUtils.CONDITION_SEVERITY[existing.name] ?? 999) : 999;
        if (!existing || newRank < oldRank) {
          ownerConditionMap[item.owner_uid] = {
            name: item.condition_name,
            description: item.condition_description
          };
        }
      });

      updateConditionPanel();
    } catch (err) {
      console.warn(`${LOG_PREFIXES.RENTAL} initial condition fetch failed:`, err.message);
      updateConditionPanel();
    }
  };

  if (startTriggerEl) {
    startTriggerEl.addEventListener('click', () => {
      if (startPicker && typeof startPicker.clear === 'function') startPicker.clear();
      startInput.value = '';
      endInput.value = '';
      updateDateSummary();
      updateRentalPrice();
      updateRentalButton();
      openCalendar('start');
    });
  }

  if (endTriggerEl) {
    endTriggerEl.addEventListener('click', () => {
      if (!startInput.value) {
        openCalendar('start');
        return;
      }
      const startDate = RentalUtils.parseDate(startInput.value);
      if (startDate && startPicker && typeof startPicker.setDate === 'function') {
        startPicker.setDate([startDate], false);
        endInput.value = '';
        renderTrigger(endDisplayEl, null);
        updateRentalPrice();
        updateRentalButton();
      }
      openCalendar('end');
    });
  }

  // Variant change wiring - anchored to the rental section form so upsell
  // and related-product forms cannot fire spurious updates on the PDP.
  const variantInput =
    document.querySelector('form.product-rental__form input[name="id"]')
    || document.querySelector('.product-rental form[action*="/cart/add"] input[name="id"]');
  if (variantInput) variantInput.addEventListener('change', updateAll);
  document.addEventListener('variant:selected', () => { updateAll(); formatBuyPrice(); });
  document.addEventListener('variant:update', () => { updateAll(); formatBuyPrice(); });

  // Bare options[] radios don't update input[name="id"] client-side; resolve
  // the picked option combination to its variant id so the UI re-runs on size change.
  const resolveVariantIdFromOptions = () => {
    if (!productOptionNames.length) return null;
    const form = variantInput?.closest('form');
    const picked = {};
    productOptionNames.forEach((name) => {
      const radio = (form || document).querySelector(`input[name="options[${name}]"]:checked`);
      if (radio) picked[name] = radio.value;
    });
    if (Object.keys(picked).length !== productOptionNames.length) return null;
    for (const vId of Object.keys(variantOptions)) {
      const opts = variantOptions[vId];
      const optionVals = [opts.option1, opts.option2, opts.option3];
      if (productOptionNames.every((name, idx) => optionVals[idx] === picked[name])) return vId;
    }
    return null;
  };
  const handleOptionChange = () => {
    const id = resolveVariantIdFromOptions();
    if (!id || !variantInput || variantInput.value === id) return;
    variantInput.value = id;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const optionScope = variantInput?.closest('form') || document;
  optionScope.querySelectorAll('input[name^="options["]').forEach((radio) => {
    radio.addEventListener('change', handleOptionChange);
  });

  // Grey out option values with no stock for the mode (RENT=rental_prices, BUY=resale_price).
  // "Matching" = same other non-Duration options, so Colour narrows Size.
  const isDurationOptionName = (name) => /^duration$/i.test(String(name || ''));
  const variantHasStockForMode = (vId, isBuying) => {
    if (isBuying) {
      return !!(variantResalePrices[vId] && parseFloat(variantResalePrices[vId].resale_price) > 0);
    }
    const rp = variantRentalPrices[vId];
    return !!(rp && Object.values(rp.day_prices || {}).some(v => parseFloat(v) > 0));
  };
  const updatePickerAvailability = () => {
    if (!productOptionNames.length) return;
    const buyRadio = document.querySelector('[data-buy-item-radio]');
    const isBuying = !!(buyRadio && buyRadio.checked);

    const checkedByOption = {};
    productOptionNames.forEach((name) => {
      if (isDurationOptionName(name)) return;
      const r = optionScope.querySelector(`input[name="options[${name}]"]:checked`);
      if (r) checkedByOption[name] = r.value;
    });

    productOptionNames.forEach((targetName, targetIndex) => {
      if (isDurationOptionName(targetName)) return;
      const availableValues = new Set();
      for (const vId of Object.keys(variantOptions)) {
        const opts = variantOptions[vId];
        const optVals = [opts.option1, opts.option2, opts.option3];
        let siblingMatch = true;
        for (let i = 0; i < productOptionNames.length; i++) {
          const name = productOptionNames[i];
          if (i === targetIndex || isDurationOptionName(name)) continue;
          if (checkedByOption[name] !== undefined && checkedByOption[name] !== optVals[i]) {
            siblingMatch = false;
            break;
          }
        }
        if (!siblingMatch) continue;
        if (!variantHasStockForMode(vId, isBuying)) continue;
        const v = optVals[targetIndex];
        if (v != null) availableValues.add(v);
      }
      optionScope.querySelectorAll(`input[name="options[${targetName}]"]`).forEach((radio) => {
        const available = availableValues.has(radio.value);
        radio.disabled = !available;
        const label = radio.closest('.variant-option__button-label');
        if (label) label.classList.toggle('is-unavailable-for-mode', !available);
      });
    });
  };

  const buyToggleRadio = document.querySelector('[data-buy-item-radio]');
  const rentToggleRadio = document.querySelector('[data-rent-item-radio]');
  if (buyToggleRadio) buyToggleRadio.addEventListener('change', updatePickerAvailability);
  if (rentToggleRadio) rentToggleRadio.addEventListener('change', updatePickerAvailability);
  optionScope.querySelectorAll('input[name^="options["]').forEach((radio) => {
    radio.addEventListener('change', updatePickerAvailability);
  });
  updatePickerAvailability();

  document.addEventListener('cart:update', refreshAvailabilityIfNeeded);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshAvailabilityIfNeeded(); });

  // Horizon-specific buy-price formatter (no-op outside Horizon)
  const priceContainer = document.querySelector('product-price [ref="priceContainer"]');
  if (priceContainer) {
    new MutationObserver(formatBuyPrice).observe(priceContainer, { childList: true, subtree: true });
  }

  updateRentalButton();
  requestAnimationFrame(() => {
    updateAll();
    formatBuyPrice();
  });

  // RENT/BUY MODE TOGGLE
  const rentItemRadio = document.querySelector('[data-rent-item-radio]');
  const buyItemRadio = document.querySelector('[data-buy-item-radio]');
  const rentalPickerShell = $('rental-picker-shell');
  const rentalDateFields = [rentalPickerShell].filter(Boolean);

  const updateRentalVisibility = () => {
    if (!rentItemRadio) return;

    const isRenting = rentItemRadio.checked;

    // Hide entire rental block (booking note, calendar, availability) on BUY
    root.style.display = isRenting ? 'flex' : 'none';

    startInput.disabled = !isRenting;
    endInput.disabled = !isRenting;
    daysEl.style.display = 'none';
    availabilityEl.style.opacity = '1';

    if (rentalPriceColumn) {
      rentalPriceColumn.style.display = 'flex';
      rentalPriceColumn.style.opacity = isRenting ? '1' : '0.6';
    }
    if (resalePriceColumn) {
      resalePriceColumn.style.display = 'flex';
      resalePriceColumn.style.opacity = isRenting ? '0.6' : '1';
    }
  };

  const updateToggleLabels = () => {
    updateAddToCartButton();
  };

  const handleModeChange = () => {
    const variantId = getVariantId();
    initializeOwnerCounts(variantId);

    updateRentalVisibility();
    updateResalePrice();
    updateHeaderPrices();
    updateAddToCartButton();
    updateToggleLabels();
    updateConditionPanel();
    if (Object.keys(ownerConditionMap || {}).length === 0 && typeof loadInitialConditions === 'function') {
      loadInitialConditions();
    }

    if (RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) {
      checkAvailability();
    } else {
      updateVariantOwners();
    }
  };

  const clearAtcError = () => {
    const errorEl = document.querySelector('.product-form-text__error');
    if (errorEl) errorEl.classList.add('hidden');
  };

  if (rentItemRadio && buyItemRadio) {
    rentItemRadio.addEventListener('change', () => { clearAtcError(); handleModeChange(); });
    buyItemRadio.addEventListener('change', () => { clearAtcError(); handleModeChange(); });
    updateRentalVisibility();

    const termsCheckbox = document.querySelector('[data-rental-terms-checkbox]');
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', updateAddToCartButton);
    }

    updateAddToCartButton();
  }

  // GLOBAL WINDOW FUNCTIONS — consumed by cart-monitor + checkout
  window.getRentalIntent = function() {
    const radio = document.querySelector('[data-rent-item-radio]');
    return radio ? radio.checked : null;
  };

  window.getRentalData = function() {
    if (!window.getRentalIntent() || !startInput || !endInput) return null;

    const start = RentalUtils.normalizeDate(startInput.value);
    const end = RentalUtils.normalizeDate(endInput.value);

    if (!RentalUtils.validateDates(startInput.value, endInput.value, dateRegex)) return null;

    const days = RentalUtils.calculateRentalDays(start, end);
    if (!days || days < 3 || days > 30) return null;

    const variantId = getVariantId();

    const rentalPrices = variantRentalPrices[variantId];
    const calculatedPrice = RentalUtils.getRentalPrice(days, rentalPrices);

    return {
      start_date: RentalUtils.formatDateForAPI(start),
      end_date: RentalUtils.formatDateForAPI(end),
      days: days,
      owner_uid: selectedOwnerUid,
      owner_name: selectedOwnerName,
      variant_id: variantId,
      price: calculatedPrice
    };
  };

  window.getResaleData = function() {
    const buyRadio = document.querySelector('[data-buy-item-radio]');
    if (!buyRadio || !buyRadio.checked) return null;

    const variantId = getVariantId();

    // Resale stock + per-item prices live on the Buy variant — target it for the hold,
    // not the selected rental variant. Fall back to the selected one if no sibling.
    const buyVariantId = resolveBuySiblingId(variantId) || variantId;

    const resaleData = getResalePricesForVariant(variantId);
    let resalePrice = resaleData?.resale_price ? parseFloat(resaleData.resale_price) : 0;

    return {
      owner_uid: selectedOwnerUid,
      owner_name: selectedOwnerName,
      variant_id: buyVariantId,
      price: resalePrice
    };
  };

  window.clearOwnerSelection = function() {
    selectedOwnerUid = null;
    selectedOwnerName = null;
    updateVariantOwners();
  };

  function setAtcMessage(msg) {
    const errorEl = document.querySelector('.product-form-text__error');
    if (!errorEl) return;
    if (!msg) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
      return;
    }
    errorEl.textContent = msg;
    errorEl.classList.remove('hidden');
  }

  function updateAddToCartButton() {
    const atc = document.querySelector('[ref="addToCartButton"]');
    if (!atc) return;

    const variantId = getVariantId();
    if (!variantId) {
      atc.disabled = true;
      setAtcMessage('');
      return;
    }

    const rentRadio = document.querySelector('[data-rent-item-radio]');
    const buyRadio = document.querySelector('[data-buy-item-radio]');
    const isRenting = rentRadio ? rentRadio.checked : true;

    const availabilityBroken = availabilityEl.classList.contains('error') || availabilityEl.classList.contains('unavailable');

    if (!isRenting) {
      // BUY mode - gate on resale price only. Rental availability state is
      // irrelevant for resale; the resale-hold endpoint validates resale
      // stock at submit time.
      const resaleData = getResalePricesForVariant(variantId);
      let resalePrice = resaleData?.resale_price ? parseFloat(resaleData.resale_price) : 0;
      atc.disabled = !resalePrice || resalePrice <= 0;
      setAtcMessage('');
      return;
    }

    // RENT mode — gate on dates, terms, availability
    const datesValid = startInput?.value && endInput?.value &&
      RentalUtils.validateDates(startInput.value, endInput.value, dateRegex);
    const termsCheckbox = document.querySelector('[data-rental-terms-checkbox]');
    const termsOk = !termsCheckbox || termsCheckbox.checked;

    if (!datesValid) {
      atc.disabled = true;
      setAtcMessage(L('msgPickDates', 'Please select your rental dates first.'));
      return;
    }
    if (!termsOk) {
      atc.disabled = true;
      setAtcMessage(L('msgTermsRequired', 'Please accept the rental terms & conditions to continue.'));
      return;
    }
    if (availabilityBroken) {
      atc.disabled = true;
      setAtcMessage('');
      return;
    }
    atc.disabled = false;
    setAtcMessage('');
  }

  // Submit loading state — apply on form submit, clear on cart event.
  const atcForLoader = document.querySelector('[ref="addToCartButton"]');
  if (atcForLoader) {
    const form = atcForLoader.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        atcForLoader.classList.add('is-loading');
        const onDone = () => {
          atcForLoader.classList.remove('is-loading');
          document.removeEventListener('cart:update', onDone);
          document.removeEventListener('cart:error', onDone);
        };
        document.addEventListener('cart:update', onDone);
        document.addEventListener('cart:error', onDone);
        setTimeout(onDone, 10000);
      });
    }
  }

  // CART BUTTON STATE OBSERVERS
  if (availabilityEl) {
    new MutationObserver(updateAddToCartButton).observe(availabilityEl, { attributes: true, attributeFilter: ['class'] });
    // Start empty and hidden — a check only runs once dates are picked. Without this the
    // leftover spinner shows on every page load (and flashes on each dev-server reload).
    availabilityEl.innerHTML = '';
    availabilityEl.style.display = 'none';
  }

  startInput?.addEventListener('input', updateAddToCartButton);
  endInput?.addEventListener('input', updateAddToCartButton);

  updateAddToCartButton();
}
